import type {
  Lesson,
  RepoState,
  QuizQuestion,
  GlossaryItem,
  CheatsheetItem,
} from "../../lib/types";

export const LESSON_PULL_REQUESTS: Lesson = {
  courseId: "pull-requests",
  courseTitle: "Pull requests on GitHub",
  id: "lesson-1",
  title: "Pull requests on GitHub",
  subtitle: "Lesson 4 of 6",
  steps: [
    {
      id: "intro",
      shortTitle: "Why a PR exists",
      shortSub: "The review surface",
      eyebrow: "Step 1 of 6",
      title: "A pull request is a proposal, not a merge.",
      sub: "On a real team, no one merges straight into main from their laptop. They push a branch to GitHub and open a pull request — a page that says \"here's what I changed, please look it over.\" The merge only happens once people approve.",
      kind: "intro",
    },
    {
      id: "push-feature",
      shortTitle: "Push a feature branch",
      shortSub: "Make the PR possible",
      eyebrow: "Step 2 of 6",
      title: "First: get a feature branch onto GitHub.",
      sub: "A PR can't exist until your work lives on its own branch on origin. The order matters — branch first, then edit. Do these in order: 1) click \"git switch -c feature\" to create and switch to a feature branch, 2) edit App.jsx, 3) git add, 4) git commit, 5) git push. Watch the new branch appear on the origin side of the graph. (Don't edit on main — PRs always come from a separate branch.)",
      kind: "sandbox",
      goal: "push",
    },
    {
      id: "open-pr",
      shortTitle: "Open the PR",
      shortSub: "Propose the merge",
      eyebrow: "Step 3 of 6",
      title: "Open a pull request from feature → main.",
      sub: "GitHub now sees a branch that isn't on main yet. The 'Create pull request' button stages the comparison — your commits, your diff, ready for review. Nothing merges yet.",
      kind: "pr",
    },
    {
      id: "review",
      shortTitle: "Review & approve",
      shortSub: "Where conversations live",
      eyebrow: "Step 4 of 6",
      title: "Reviewers leave comments. You approve.",
      sub: "This is where most of a PM's PR time is spent: reading the diff, asking questions in the conversation, and approving when it looks right. Leave a comment, then approve to unlock the merge.",
      kind: "pr",
    },
    {
      id: "merge-pr",
      shortTitle: "Merge the PR",
      shortSub: "Move work into main",
      eyebrow: "Step 5 of 6",
      title: "Approved? Merge it.",
      sub: "Once a PR is approved (and CI is green on a real repo), the merge button lights up. Click it and your feature commits land on origin/main — visible to the whole team.",
      kind: "pr",
    },
    {
      id: "wrap",
      shortTitle: "Quiz & recap",
      shortSub: "Knowledge check",
      eyebrow: "Wrap-up",
      title: "Branch, push, PR, review, merge.",
      sub: "That's the everyday loop on a team. Quick check below, then cheatsheet + glossary.",
      kind: "wrap",
    },
  ],
};


export const QUIZ_QUESTIONS_PULL_REQUESTS: QuizQuestion[] = [
  {
    q: "What does opening a pull request actually do?",
    options: [
      { t: "Merges your branch into main immediately", correct: false },
      {
        t: "Proposes a merge and opens a page for review — nothing merges yet",
        correct: true,
      },
      { t: "Deletes your feature branch from origin", correct: false },
      { t: "Pushes your local commits to GitHub", correct: false },
    ],
    why: "Right — a PR is a proposal. The diff is staged for review, but no commits land on main until someone clicks Merge.",
    wrongWhy:
      "A PR doesn't merge anything by itself. It opens a page that compares your branch to the target, lets people comment and approve, and *then* you click Merge.",
  },
  {
    q: "Your teammate says \"I approved your PR but left a comment about the variable name.\" Can you merge?",
    options: [
      { t: "No — any comment blocks merging until resolved", correct: false },
      {
        t: "Yes — approval unlocks merge. Comments are conversation, not blockers",
        correct: true,
      },
      { t: "Only if you reply to the comment first", correct: false },
      { t: "No — you need a second reviewer first", correct: false },
    ],
    why: "Exactly. A plain comment is a note. Only \"Request changes\" actually blocks the merge button. The teammate's approval is the green light.",
    wrongWhy:
      "Comments by themselves don't block anything. The merge button is gated by approvals and (if configured) by CI — not by whether every comment has been replied to. Only an explicit \"Request changes\" review blocks merge.",
  },
  {
    q: "After your PR merges, where do your commits end up?",
    options: [
      { t: "On your local main automatically", correct: false },
      { t: "Only on the feature branch, forever", correct: false },
      {
        t: "On origin/main — your local main is now behind until you pull",
        correct: true,
      },
      { t: "They're rewritten into a single new commit only you can see", correct: false },
    ],
    why: "Yes — the merge happens on GitHub, so origin/main moves forward. Your laptop's main is now behind. A quick git pull catches you up.",
    wrongWhy:
      "The merge happens on GitHub's copy of the repo, not yours. Origin/main moves forward; your local main stays where it was until you pull. That's why \"git pull\" right after a merge is a habit.",
  },
];

export const GLOSSARY_PULL_REQUESTS: GlossaryItem[] = [
  { term: "Pull request (PR)", def: "A proposal to merge one branch into another, with a GitHub page for review. \"MR\" on GitLab means the same thing." },
  { term: "Review", def: "A reviewer's verdict on a PR: Approve, Comment, or Request changes." },
  { term: "Approve", def: "A review state that says \"this is fine to merge.\" Most repos need at least one." },
  { term: "Request changes", def: "A review state that blocks the merge button until the author pushes a fix and the reviewer re-reviews." },
  { term: "Branch protection", def: "Repo rules that gate main — usually \"PR required, at least one approval, CI must pass.\" The reason you can't push straight to main." },
  { term: "Squash merge", def: "Collapses all of a PR's commits into one before landing on main. Keeps history flat. Nymbl's default." },
  { term: "Merge commit", def: "Keeps every PR commit and adds a \"Merge pull request #N\" commit on top of main." },
  { term: "Draft PR", def: "A PR marked as work-in-progress — visible but not ready for review. Convert to \"Ready for review\" when it's done." },
  { term: "CI", def: "Continuous integration — automated checks (tests, lint, build) that run on every PR. Usually a required gate before merge." },
];

export const CHEATSHEET_PULL_REQUESTS: CheatsheetItem[] = [
  { cmd: "git push -u origin feature", desc: "Push your branch and remember the upstream — required before opening a PR." },
  { cmd: "GitHub UI: Compare & pull request", desc: "After pushing a branch, GitHub shows this button. Fill in title + description, click Create." },
  { cmd: "gh pr create", desc: "Open a PR from the terminal. Uses your current branch and pushes if needed." },
  { cmd: "gh pr view --web", desc: "Open the current branch's PR in your browser." },
  { cmd: "gh pr review --approve", desc: "Approve the current PR from the terminal." },
  { cmd: "gh pr merge --squash", desc: "Squash-merge the current PR. Add --delete-branch to also remove the feature branch." },
  { cmd: "git pull", desc: "Catch your local main up after a PR merges on GitHub." },
];

const APP_JSX = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>Welcome to Atlas</h1>
        <p>Your Business Operating System.</p>
      </main>
    </div>
  );
}
`;

const HEADER_JSX = `export default function Header() {
  return (
    <header className="header">
      <img src="/logo.png" alt="logo" />
      <nav>
        <a href="/">Home</a>
        <a href="/about">About</a>
      </nav>
    </header>
  );
}
`;

const README_MD = `# Atlas

Internal business operating system.

Built by the team at Nymbl.
`;

/**
 * Initial state for the pull-requests lesson.
 *
 * Local: already cloned — main mirrors origin's three commits. The learner
 * starts ready to branch off and make a change. (We skip re-teaching clone;
 * that was Course 03.)
 */
export const INITIAL_REPO_PULL_REQUESTS: RepoState = {
  commits: [
    { id: "a1b2c3d", msg: "Initial commit", branch: "main", parent: null, x: 0 },
    { id: "e4f5a6b", msg: "Add Header component", branch: "main", parent: "a1b2c3d", x: 1 },
    { id: "c7d8e9f", msg: "Wire up homepage layout", branch: "main", parent: "e4f5a6b", x: 2 },
  ],
  branches: { main: "c7d8e9f" },
  head: "main",
  files: {
    "App.jsx": { content: APP_JSX, committed: APP_JSX, staged: null, status: "clean" },
    "Header.jsx": { content: HEADER_JSX, committed: HEADER_JSX, staged: null, status: "clean" },
    "README.md": { content: README_MD, committed: README_MD, staged: null, status: "clean" },
  },
  remotes: {
    origin: {
      branches: { main: "c7d8e9f" },
      commits: [
        { id: "a1b2c3d", msg: "Initial commit", branch: "main", parent: null, x: 0 },
        { id: "e4f5a6b", msg: "Add Header component", branch: "main", parent: "a1b2c3d", x: 1 },
        { id: "c7d8e9f", msg: "Wire up homepage layout", branch: "main", parent: "e4f5a6b", x: 2 },
      ],
      files: {
        "App.jsx": APP_JSX,
        "Header.jsx": HEADER_JSX,
        "README.md": README_MD,
      },
    },
  },
  tracking: { main: { remote: "origin", branch: "main" } },
};

export const INITIAL_LOG_PULL_REQUESTS = [
  { kind: "out" as const, text: "Repo cloned. Local main matches origin/main at c7d8e9f." },
];

/** The original App.jsx — used for diff display in the PR panel. */
export const ORIGINAL_APP_JSX = APP_JSX;
